import React, { useState, useEffect, useRef } from 'react';
import './SHCommentBox.css';
import Services from '../../../../services/Services';
import { useLanguage } from '../../../../LanguageProvider';
import { IoAdd } from 'react-icons/io5';

const SHCommentBox = ({
  comments,
  file,
  getCollectionDetail,
  onCommentSubmit,
  placeholder,
  enableLikes = true,
  likes
}) => {
  const { getTranslation } = useLanguage();
  const token = localStorage.getItem('token');
  const [localComments, setLocalComments] = useState([]);
  const [comment, setComment] = useState('');
  const listRef = useRef(null);
  const [activeView, setActiveView] = useState('comments');
  const commentPlaceholder = placeholder || getTranslation('writeSomethingAboutTheEvent');

  // Normalize & sort comments (old -> new)
  useEffect(() => {
    if (Array.isArray(comments)) {
      const sorted = [...comments].sort((a, b) => Number(a.createdDateTime) - Number(b.createdDateTime));
      const normalized = sorted.map(c => ({
        id: c.id,
        author: `${c.userInfos?.name || ''} ${c.userInfos?.lastname || ''}`.trim(),
        avatar: c.userInfos?.userAvatar || '',
        createdAt: Number(c.createdDateTime) ? Number(c.createdDateTime) * 1000 : Date.now(),
        text: c.content
      }));
      setLocalComments(normalized);
    } else {
      setLocalComments([]);
    }
  }, [comments]);

  // Scroll to bottom when new comment arrives
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [localComments]);

  const sentComment = (message) => {
    if (onCommentSubmit) {
      return onCommentSubmit(message).catch(console.error);
    }

    if (file) {
      return Services.SocialHiveCommentOnFile(
        token,
        file.channelID,
        file.collectionID,
        file.fileID,
        message
      )
        .then(res => {
          if (res.data.status && typeof getCollectionDetail === 'function') {
            getCollectionDetail(file.collectionID);
          }
        })
        .catch(console.error);
    }

    return Promise.resolve();
  };

  const handleSubmit = () => {
    const text = comment.trim();
    if (!text) return;
    // Optimistic UI
    const user = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user'));
    const author = `${user.name || 'Anonim'} ${user.lastname || ''}`.trim();
    const avatar = user?.userAvatar || '';
    const createdAt = Date.now();
    sentComment(text);
    setLocalComments(prev => [...prev, { id: Date.now(), author, avatar, createdAt, text }]);
    setComment('');
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  function getFirstAndLastInitials(sentence) {
    const words = sentence
      .trim()
      .split(/\s+/)
      .filter(w => w.length > 0);
    if (words.length === 0) return '';
    const firstInitial = words[0][0];
    const lastInitial = words[words.length - 1][0];
    return (firstInitial + lastInitial).toUpperCase();
  }

  function getAvatarColor(userName) {
    if (!userName) return 'var(--avatar-color-1)';
    const initials = getFirstAndLastInitials(userName);
    if (!initials) return 'var(--avatar-color-1)';
    // Use a simple hash to consistently map to one of 4 colors
    const hash = initials.charCodeAt(0) + (initials.length > 1 ? initials.charCodeAt(1) : 0);
    const colorIndex = (hash % 4) + 1;
    return `var(--avatar-color-${colorIndex})`;
  }

  const formatTime = (ts) => {
    try {
      return new Date(ts).toLocaleString();
    } catch (e) {
      return '';
    }
  };

  // Prepare likes array (file.likes may be false)
  const likesArray = Array.isArray(likes)
    ? likes
    : Array.isArray(file?.likes)
      ? file.likes
      : [];
  const showLikesTab = Boolean(enableLikes);
  const tabs = [
    { key: 'comments', label: getTranslation('comments') },
    ...(showLikesTab ? [{ key: 'likes', label: getTranslation('likes') }] : [])
  ];

  useEffect(() => {
    if (!showLikesTab && activeView === 'likes') {
      setActiveView('comments');
    }
  }, [showLikesTab, activeView]);

  return (
    <div className="sh-comment-box">
      <div className="social-hive-header">
        <nav className="social-hive-header__nav" style={{ borderBottomWidth: '1px' }}>
          <div className="social-hive-header__nav-list" style={{ paddingLeft: '26px' }}>
            {tabs.map(tab => (
              <div
                key={tab.key}
                className={`social-hive-header__nav-item${activeView === tab.key ? ' active' : ''}`}
                style={{ fontSize: '12px' }}
                onClick={() => setActiveView(tab.key)}
              >
                {tab.label}
              </div>
            ))}
          </div>
        </nav>
      </div>

      {activeView === 'comments' ? (
        <>
          <div className="sh-comments-list" ref={listRef} style={{ marginTop: '15px' }}>
            {localComments.map(c => (
              <div className="sh-comment-item" key={c.id}>
                {c.avatar ? (
                  <img src={c.avatar} alt={c.author} className="sh-comment-avatar" />
                ) : (
                  <div 
                    className="social-hive-chat__avatar" 
                    style={{ 
                      width: '28px', 
                      height: '28px', 
                      fontSize: '12px',
                      backgroundColor: getAvatarColor(c.author)
                    }}
                  >
                    {getFirstAndLastInitials(c.author)}
                  </div>
                )}
                <div className="sh-comment-content">
                  <div className="sh-comment-meta">
                    <span className="sh-comment-author">{c.author}</span>
                    <span className="sh-comment-time">{formatTime(c.createdAt)}</span>
                  </div>
                  <div className="sh-comment-text">{c.text}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="sh-comment-input-wrapper">
            <input
              type="text"
              className="sh-comment-input"
              placeholder={commentPlaceholder}
              value={comment}
              onChange={e => setComment(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="sh-comment-submit"
              onClick={handleSubmit}
              disabled={!comment.trim()}
            >
              <IoAdd size={16} />
            </button>
          </div>
        </>
      ) : (
        showLikesTab && (
          <>
            <div className="sh-likes-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '15px' }}>
              {likesArray.length > 0 && likesArray.map(like => {
                const info = like.userInfos || {};
                const name = `${info.name || ''} ${info.lastname || ''}`.trim();
                return (
                  <div
                    key={like.id}
                    className="sh-like-item"
                    style={{ display: 'flex', alignItems: 'center', padding: '4px 8px', color:'var(--alert-box-button-font-color)' }}
                  >
                    {info.userAvatar ? (
                      <img
                        src={info.userAvatar}
                        alt={name}
                        className="sh-like-avatar"
                        style={{ width: '24px', height: '24px', borderRadius: '50%', marginRight: '6px' }}
                      />
                    ) : (
                      <div
                        className="social-hive-chat__avatar"
                        style={{ 
                          width: '25px', 
                          height: '25px',
                          marginRight:'6px',
                          fontSize:'12px',
                          backgroundColor: getAvatarColor(name)
                        }}
                      >
                        {getFirstAndLastInitials(`${info.name + " " + info.lastname}`)}
                      </div>
                    )}
                    <span className="sh-like-name" style={{ fontSize: '12px' }}>{name}</span>
                  </div>
                );
              })}
              
            </div>
          </>
        )
      )}
    </div>
  );
};

export default SHCommentBox;
